import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getNotaryById } from '../../services/notaryService';

const SPECIALIZATIONS = [
  { key: 'vente_immobiliere', label: 'Vente immobilière', icon: 'Home' },
  { key: 'succession', label: 'Successions', icon: 'ScrollText' },
  { key: 'droit_famille', label: 'Droit de la famille', icon: 'Users' },
  { key: 'droit_commercial', label: 'Droit commercial', icon: 'Briefcase' },
  { key: 'urbanisme', label: 'Urbanisme', icon: 'Building2' },
  { key: 'baux', label: 'Baux & locations', icon: 'Key' },
];

const MOCK_TESTIMONIALS = [
  {
    id: 1,
    author: 'Kouamé A.',
    location: 'Cocody, Abidjan',
    rating: 5,
    date: '2026-06-15',
    text: 'Processus de vente géré avec une rigueur exemplaire. Les délais ont été respectés et toutes les démarches expliquées clairement. Je recommande vivement.',
    transaction_type: 'Vente appartement',
    verified: true,
  },
  {
    id: 2,
    author: 'Fatou D.',
    location: 'Marcory, Abidjan',
    rating: 5,
    date: '2026-05-28',
    text: 'Très professionnel et disponible. La succession a été traitée avec beaucoup de sensibilité et d\'efficacité. Merci pour votre accompagnement.',
    transaction_type: 'Succession',
    verified: true,
  },
  {
    id: 3,
    author: 'Ibrahim S.',
    location: 'Plateau, Abidjan',
    rating: 4,
    date: '2026-04-10',
    text: 'Excellent suivi du dossier d\'achat. Quelques délais supplémentaires liés aux documents administratifs, mais tout a été bien géré.',
    transaction_type: 'Achat terrain',
    verified: true,
  },
  {
    id: 4,
    author: 'Marie-Claire K.',
    location: 'Yopougon, Abidjan',
    rating: 5,
    date: '2026-03-22',
    text: 'Accompagnement remarquable pour notre contrat de mariage. Explications claires, écoute attentive. Un vrai professionnel de confiance.',
    transaction_type: 'Contrat de mariage',
    verified: true,
  },
];

const MOCK_PERFORMANCE = {
  overall_score: 4.8,
  total_reviews: 47,
  response_time_hours: 3.2,
  completion_rate: 96,
  on_time_rate: 91,
  client_satisfaction: 98,
  specialization_scores: {
    vente_immobiliere: 4.9,
    succession: 4.7,
    droit_famille: 4.8,
    droit_commercial: 4.6,
    urbanisme: 4.5,
    baux: 4.8,
  },
  rating_distribution: { 5: 38, 4: 7, 3: 2, 2: 0, 1: 0 },
  transactions_completed: 124,
  years_experience: 12,
};

const StarRating = ({ rating, size = 'sm', showNumber = false }) => {
  const stars = [1, 2, 3, 4, 5];
  const sizeClass = size === 'lg' ? 'w-5 h-5' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {stars?.map(s => (
        <svg key={s} className={`${sizeClass} ${s <= Math.round(rating) ? 'text-brand-gold' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {showNumber && <span className="ml-1 text-ios-caption1 font-system font-body-semibold text-text-secondary">{rating?.toFixed(1)}</span>}
    </div>
  );
};

const ScoreRing = ({ score, label, color = 'brand-earth' }) => {
  const pct = (score / 5) * 100;
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
          <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor"
            className={`text-${color}`}
            strokeWidth="5" strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[13px] font-display font-display-semibold text-text-primary">{score?.toFixed(1)}</span>
        </div>
      </div>
      <p className="text-[10px] font-system text-text-secondary text-center leading-tight max-w-[64px]">{label}</p>
    </div>
  );
};

const MetricCard = ({ icon, value, label, sub = null, accent = 'brand-earth' }) => (
  <div className="bg-white rounded-2xl border border-border/50 p-4 flex items-start space-x-3">
    <div className={`w-10 h-10 rounded-xl bg-${accent}/10 flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={18} className={`text-${accent}`} />
    </div>
    <div className="min-w-0">
      <p className="text-ios-title3 font-display font-display-semibold text-text-primary leading-tight">{value}</p>
      <p className="text-[11px] font-system text-text-secondary mt-0.5">{label}</p>
      {sub && <p className="text-[10px] font-system text-text-secondary mt-0.5 opacity-70">{sub}</p>}
    </div>
  </div>
);

const TestimonialCard = ({ testimonial }) => (
  <div className="bg-white rounded-2xl border border-border/50 p-5 space-y-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-gold/30 to-brand-earth/20 flex items-center justify-center flex-shrink-0">
          <span className="text-ios-callout font-display font-display-semibold text-brand-earth">
            {testimonial?.author?.[0]}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-ios-callout font-system font-body-semibold text-text-primary">{testimonial?.author}</p>
            {testimonial?.verified && (
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 border border-green-200 rounded-full">
                <Icon name="BadgeCheck" size={10} className="text-green-600" />
                <span className="text-[9px] font-system font-body-semibold text-green-700">Vérifié</span>
              </div>
            )}
          </div>
          <p className="text-ios-caption1 text-text-secondary">{testimonial?.location}</p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <StarRating rating={testimonial?.rating} size="sm" />
        <span className="text-[10px] text-text-secondary font-system">
          {new Date(testimonial?.date)?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
        </span>
      </div>
    </div>
    <p className="text-ios-callout font-system text-text-primary leading-relaxed">"{testimonial?.text}"</p>
    <div className="flex items-center gap-1.5">
      <div className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
      <span className="text-[11px] font-system text-text-secondary">{testimonial?.transaction_type}</span>
    </div>
  </div>
);

const NotaryPerformancePage = () => {
  const { notaryId } = useParams();
  const [notary, setNotary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const perf = MOCK_PERFORMANCE;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      if (notaryId) {
        const { data } = (await getNotaryById?.(notaryId)) || {};
        if (data) setNotary(data);
      }
      setLoading(false);
    };
    load();
  }, [notaryId]);

  const totalRatings = Object.values(perf?.rating_distribution || {})?.reduce((a, b) => a + b, 0);

  const TABS = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'BarChart2' },
    { id: 'specializations', label: 'Spécialisations', icon: 'Award' },
    { id: 'testimonials', label: 'Témoignages', icon: 'MessageSquare' },
  ];

  return (
    <>
      <Helmet>
        <title>Performance Notaire – Kapital States</title>
        <meta name="description" content="Scores de performance, témoignages clients et évaluations de spécialisation du notaire." />
      </Helmet>
      <div className="min-h-screen bg-surface">
        {/* Header */}
        <header className="bg-white border-b border-border/50 sticky top-0 z-40 shadow-ios-light">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/notary-appointment-booking-system"
                className="w-9 h-9 rounded-xl border border-border/50 flex items-center justify-center hover:bg-slate-50 transition-colors">
                <Icon name="ArrowLeft" size={16} className="text-text-secondary" />
              </Link>
              <div>
                <p className="text-ios-callout font-system font-body-semibold text-text-primary leading-tight">Performance Notaire</p>
                <p className="text-[10px] text-text-secondary font-system">Évaluation post-transaction</p>
              </div>
            </div>
            <Link to="/notary-appointment-booking-system"
              className="flex items-center space-x-1.5 px-3 py-2 bg-brand-earth text-white rounded-xl text-ios-caption1 font-system font-body-semibold hover:bg-brand-earth/90 transition-colors">
              <Icon name="CalendarPlus" size={13} />
              <span className="hidden sm:inline">Prendre RDV</span>
              <span className="sm:hidden">RDV</span>
            </Link>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Notary Profile Hero */}
          <div className="bg-white rounded-2xl border border-border/50 overflow-hidden">
            {/* Top gradient band */}
            <div className="h-2 bg-gradient-to-r from-brand-gold via-brand-earth to-brand-gold/60" />
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-brand-gold/20 to-brand-earth/10 border border-brand-gold/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Scale" size={28} className="text-brand-earth" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <h1 className="text-ios-title2 font-display font-display-semibold text-text-primary leading-tight">
                        {notary?.notary_name || 'Maître Jean-Baptiste Kouassi'}
                      </h1>
                      <p className="text-ios-callout text-text-secondary font-system mt-0.5">
                        {notary?.office_name || 'Étude Notariale Kouassi & Associés'}
                      </p>
                      <p className="text-ios-caption1 text-text-secondary font-system mt-0.5 flex items-center gap-1">
                        <Icon name="MapPin" size={11} />
                        {notary?.office_address || 'Plateau, Abidjan'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="text-[11px] font-system font-body-semibold text-green-700">Disponible</span>
                      </div>
                      <span className="text-[10px] text-text-secondary font-system">{perf?.years_experience} ans d'expérience</span>
                    </div>
                  </div>

                  {/* Score summary */}
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <StarRating rating={perf?.overall_score} size="md" />
                      <span className="text-ios-title3 font-display font-display-semibold text-text-primary">{perf?.overall_score}</span>
                      <span className="text-ios-caption1 text-text-secondary font-system">({perf?.total_reviews} avis)</span>
                    </div>
                    <div className="w-px h-4 bg-border/50 hidden sm:block" />
                    <div className="flex items-center gap-1.5">
                      <Icon name="CheckCircle2" size={13} className="text-green-600" />
                      <span className="text-ios-caption1 font-system text-green-700 font-body-semibold">{perf?.transactions_completed} transactions</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab navigation */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {TABS?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                className={`flex-shrink-0 flex items-center space-x-2 px-4 py-2.5 rounded-xl text-ios-callout font-system font-body-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-brand-earth text-white shadow-ios-light'
                    : 'bg-white border border-border/50 text-text-secondary hover:border-brand-gold/40'
                }`}>
                <Icon name={tab?.icon} size={15} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Key metrics grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard icon="Clock" value={`${perf?.response_time_hours}h`} label="Temps de réponse moyen" accent="blue-600" />
                <MetricCard icon="TrendingUp" value={`${perf?.completion_rate}%`} label="Taux de complétion" accent="green-600" />
                <MetricCard icon="Timer" value={`${perf?.on_time_rate}%`} label="Respect des délais" accent="brand-earth" />
                <MetricCard icon="Heart" value={`${perf?.client_satisfaction}%`} label="Satisfaction client" accent="rose-500" />
              </div>

              {/* Score rings */}
              <div className="bg-white rounded-2xl border border-border/50 p-5">
                <h3 className="text-ios-callout font-system font-body-semibold text-text-primary mb-4 flex items-center space-x-2">
                  <Icon name="Target" size={16} className="text-brand-earth" />
                  <span>Scores de performance</span>
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 justify-items-center">
                  {SPECIALIZATIONS?.map(spec => (
                    <ScoreRing
                      key={spec?.key}
                      score={perf?.specialization_scores?.[spec?.key] || 4.5}
                      label={spec?.label}
                      color="brand-earth"
                    />
                  ))}
                </div>
              </div>

              {/* Rating distribution */}
              <div className="bg-white rounded-2xl border border-border/50 p-5">
                <h3 className="text-ios-callout font-system font-body-semibold text-text-primary mb-4 flex items-center space-x-2">
                  <Icon name="BarChart2" size={16} className="text-brand-earth" />
                  <span>Distribution des notes</span>
                </h3>
                <div className="flex gap-5 items-center">
                  {/* Big score */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <p className="text-[48px] font-display font-display-semibold text-text-primary leading-none">{perf?.overall_score}</p>
                    <StarRating rating={perf?.overall_score} size="md" />
                    <p className="text-ios-caption1 text-text-secondary font-system mt-1">{perf?.total_reviews} avis</p>
                  </div>
                  {/* Bars */}
                  <div className="flex-1 space-y-1.5">
                    {[5, 4, 3, 2, 1]?.map(star => {
                      const count = perf?.rating_distribution?.[star] || 0;
                      const pct = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-[11px] font-system text-text-secondary w-3 text-right flex-shrink-0">{star}</span>
                          <svg className="w-3 h-3 text-brand-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-gold rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] font-system text-text-secondary w-6 flex-shrink-0">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { icon: 'ShieldCheck', label: 'Notaire certifié', sub: 'Chambre des Notaires CI', color: 'text-green-600', bg: 'bg-green-50' },
                  { icon: 'Award', label: 'Top Performer 2025', sub: 'Kapital States', color: 'text-brand-earth', bg: 'bg-brand-gold/10' },
                  { icon: 'Star', label: 'Avis vérifiés', sub: `${perf?.total_reviews} témoignages`, color: 'text-amber-600', bg: 'bg-amber-50' },
                ]?.map((badge, i) => (
                  <div key={i} className={`${badge?.bg} rounded-2xl border border-current/10 p-4 flex items-center space-x-3`}>
                    <Icon name={badge?.icon} size={20} className={badge?.color} />
                    <div>
                      <p className={`text-ios-caption1 font-system font-body-semibold ${badge?.color}`}>{badge?.label}</p>
                      <p className="text-[10px] text-text-secondary font-system">{badge?.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specializations Tab */}
          {activeTab === 'specializations' && (
            <div className="space-y-4">
              <p className="text-ios-callout font-system text-text-secondary">
                Évaluations détaillées par domaine de spécialisation, basées sur les retours clients post-transaction.
              </p>
              <div className="space-y-3">
                {SPECIALIZATIONS?.map(spec => {
                  const score = perf?.specialization_scores?.[spec?.key] || 4.5;
                  const pct = (score / 5) * 100;
                  return (
                    <div key={spec?.key} className="bg-white rounded-2xl border border-border/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-xl bg-brand-gold/10 flex items-center justify-center">
                            <Icon name={spec?.icon} size={16} className="text-brand-earth" />
                          </div>
                          <div>
                            <p className="text-ios-callout font-system font-body-semibold text-text-primary">{spec?.label}</p>
                            <StarRating rating={score} size="sm" showNumber />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-ios-title3 font-display font-display-semibold text-brand-earth">{score?.toFixed(1)}</p>
                          <p className="text-[10px] text-text-secondary font-system">/ 5.0</p>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-brand-gold to-brand-earth transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1.5">
                        <span className="text-[10px] text-text-secondary font-system">Insuffisant</span>
                        <span className="text-[10px] text-text-secondary font-system">Excellent</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Expertise summary */}
              <div className="bg-gradient-to-br from-brand-gold/5 to-brand-earth/5 rounded-2xl border border-brand-gold/20 p-5">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Lightbulb" size={16} className="text-brand-earth" />
                  <h3 className="text-ios-callout font-system font-body-semibold text-text-primary">Domaines d'excellence</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SPECIALIZATIONS?.filter(s => (perf?.specialization_scores?.[s?.key] || 0) >= 4.7)?.map(s => (
                    <span key={s?.key} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-gold/30 rounded-full text-[11px] font-system font-body-semibold text-brand-earth">
                      <Icon name={s?.icon} size={11} />
                      {s?.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-ios-callout font-system text-text-secondary">
                  {MOCK_TESTIMONIALS?.length} témoignages vérifiés
                </p>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                  <Icon name="ShieldCheck" size={11} className="text-green-600" />
                  <span className="text-[10px] font-system font-body-semibold text-green-700">Tous vérifiés</span>
                </div>
              </div>
              <div className="space-y-3">
                {MOCK_TESTIMONIALS?.map(t => (
                  <TestimonialCard key={t?.id} testimonial={t} />
                ))}
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-brand-earth to-brand-earth/80 rounded-2xl p-5 text-white text-center">
                <Icon name="CalendarPlus" size={24} className="mx-auto mb-2 opacity-90" />
                <h3 className="text-ios-headline font-display font-display-semibold mb-1">Prêt à travailler avec ce notaire ?</h3>
                <p className="text-ios-callout opacity-80 mb-4">Réservez une consultation en ligne en quelques minutes.</p>
                <Link to="/notary-appointment-booking-system"
                  className="inline-flex items-center space-x-2 px-5 py-2.5 bg-white text-brand-earth rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-gold/10 transition-colors">
                  <Icon name="Calendar" size={15} />
                  <span>Prendre rendez-vous</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotaryPerformancePage;
